import { useMemo } from "react";
import { LogicalNode } from "app/types/record-management-types";

import SysColumn from "app/class/sys-column";
import SysRecord from "app/class/record";
import SysTable from "app/class/sys-table";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";

import { ResolvedRelatedList } from "../types";

const buildTableNameMap = (tables: SysTable[]) =>
    new Map(tables.map((table) => [table.id, table.name]));

const buildColumnNameMap = (columns: SysColumn[]) =>
    new Map(
        columns.map((column) => [
            `${column.sys_table_id}:${column.id}`,
            column.name,
        ])
    );

export const useResolvedRelatedLists = (relatedLists: SysRecord[]): ResolvedRelatedList[] => {
    const { data: tables = [] } = useTables();
    const { data: columns = [] } = useColumns();

    return useMemo(() => {
        const tableNameMap = buildTableNameMap(tables);
        const columnNameMap = buildColumnNameMap(columns);

        return relatedLists.map((relatedList) => ({
            definition: relatedList,
            relationshipType:
                relatedList.relationship_type === "many_to_many" ? "many_to_many" :
                relatedList.relationship_type === "inheritance_ancestors" ? "inheritance_ancestors" : "direct",
            tableName: tableNameMap.get(relatedList.target_table) || "_",
            targetColumnName:
                columnNameMap.get(`${relatedList.target_table}:${relatedList.target_column}`) || "",
            queryParams: null as LogicalNode | null,
            allowNew: Boolean(relatedList.allow_new),
            newMode: relatedList.new_mode || "",
            allowEditRelationships: Boolean(relatedList.allow_edit_relationships),
            bridge:
                relatedList.relationship_type === "many_to_many"
                    ? {
                        tableName: tableNameMap.get(relatedList.bridge_table) || "_",
                        sourceColumnName:
                            columnNameMap.get(`${relatedList.bridge_table}:${relatedList.bridge_source_column}`) || "",
                        targetColumnName:
                            columnNameMap.get(`${relatedList.bridge_table}:${relatedList.bridge_target_column}`) || "",
                    }
                    : undefined,
        }));
    }, [columns, relatedLists, tables]);
};
